import { FIVE_STAR_SCALE, TEN_POINT_SCALE } from './constants';
import type {
  DifficultyVariant,
  FixtureCellData,
  GameweekFixtures
} from './constants';

const HALF_STAR_RESOLUTION = 2;
// Per-fixture difficulty spans the full ten-point scale, so the five colour
// bands split it evenly; clipping the top of the scale would render e.g. a 7
// and a 10 identically.
const VERY_EASY_THRESHOLD = 2;
const EASY_THRESHOLD = 4;
const MODERATE_THRESHOLD = 6;
const HARD_THRESHOLD = 8;

const DIFFICULTY_COLOUR_CLASSES: Record<DifficultyVariant, string> = {
  'very-easy': 'bg-[#A9D3CA]',
  easy: 'bg-[#DAECE8]',
  moderate: 'bg-[#F3E7DA]',
  hard: 'bg-[#F7D9E3]',
  'very-hard': 'bg-[#F2BFCF]'
};

export const ratingToStars = (rating: number): number => {
  const clamped = Math.min(Math.max(rating, 0), TEN_POINT_SCALE);
  return (
    Math.round(
      (clamped / TEN_POINT_SCALE) * FIVE_STAR_SCALE * HALF_STAR_RESOLUTION
    ) / HALF_STAR_RESOLUTION
  );
};

export const ratingToDifficulty = (rating: number): DifficultyVariant => {
  if (rating <= VERY_EASY_THRESHOLD) return 'very-easy';
  if (rating <= EASY_THRESHOLD) return 'easy';
  if (rating <= MODERATE_THRESHOLD) return 'moderate';
  if (rating <= HARD_THRESHOLD) return 'hard';
  return 'very-hard';
};

export const difficultyToColourClass = (variant: DifficultyVariant): string =>
  DIFFICULTY_COLOUR_CLASSES[variant];

const compareByKickoff = (a: FixtureCellData, b: FixtureCellData): number => {
  // Fixtures without a confirmed kickoff sort to the end so that the
  // confirmed leg of a double gameweek shows up first.
  const aTime = a.kickoffAt ? new Date(a.kickoffAt).getTime() : Infinity;
  const bTime = b.kickoffAt ? new Date(b.kickoffAt).getTime() : Infinity;
  return aTime - bTime;
};

export const groupFixturesByGameweek = (
  fixtures: FixtureCellData[],
  gameweekStart: number,
  gameweekEnd: number
): GameweekFixtures[] => {
  const grouped: GameweekFixtures[] = [];

  for (let gameweek = gameweekStart; gameweek <= gameweekEnd; gameweek++) {
    grouped.push({
      gameweek,
      fixtures: fixtures
        .filter((fixture) => fixture.gameweek === gameweek)
        .sort(compareByKickoff)
    });
  }

  return grouped;
};
